import React from 'react';
import PropTypes from 'prop-types';

import { 
  default as useLocalStorageState,
}                         from 'use-local-storage-state';

import {
  FieldValue
}                     from '../../../packages/detail-panel';

import {
  ResourceCard,
  ResourceRef,
}                     from '../../resource';

import { 
  fmtIso8601AsLocalDateTime,
}                     from '../../utils/formatters';

import { 
  SnackbarAlert,
}                     from '../../utils/components';

import { 
  useWorkloadDataConfiguration,
}                     from '../hooks';

import { 
  fmtDataConfigurationRefs 
}                     from '../formatters';

import {
  Executions,
  StartExecutionIcon,
  StartExecutionDialog,
  startExecution,
}                     from  '../../execution';

import { 
  ContainerChip 
}                     from '.';

export default function WorkloadCard(props) {

  const [openStartExecution, setOpenStartExecution] = React.useState(false);
  const [expandExecutions, doExpandExecutions] = React.useState(0);

  const [startExecutionMessage, setStartExecutionMessage] = React.useState({});
  const [showStartExecutionMessage, setShowStartExecutionMessage] = React.useState(false);

  const [executionsExpanded, setExecutionsExpanded] = useLocalStorageState("workload_ee", false);
  
  const { resource } = props;
  const { resource: dataConfiguration } = useWorkloadDataConfiguration(resource?resource.ResourceId:null);

  React.useEffect(() => {
    if (startExecutionMessage.message) {
      setShowStartExecutionMessage(true);
    }
  }, [startExecutionMessage]);

  if(!resource) return(null); 
  if(!dataConfiguration) return(null); 
  
  const handleExpandClick = (value) => {
    setExecutionsExpanded(value);
  };

  const handleStartExecutionDialogOpen = () => {
    setOpenStartExecution(true);
  };

  const handleStartExecutionDialogClose = () => {
    setOpenStartExecution(false);
  };
  
  const handleCloseStartExecutionMessage = () => {
    setShowStartExecutionMessage(false);
  };
  
  const handleStartExecution = async (args) => {
    setStartExecutionMessage({ 
      message: "Starting execution...",
      severity: "info",
      duration: 3000,
    });
    handleStartExecutionDialogClose();
    const { succeeded } = await startExecution(resource.ResourceId, args);
    if(succeeded) {
      setStartExecutionMessage({ 
        message: "Started execution",
        severity: "success",
      });
      doExpandExecutions(e => e+1);
    } else {
      setStartExecutionMessage({ 
        message: "Unable to start execution!",
        severity: "error",
      });
    }
  };
  
  const executionsColumnDefs = [
    { name: 'ClientAccountId', options: { display: false } },
    { name: 'WorkloadId', options: { display: false } },
    { name: 'StartTime', options: { display: false } },
  ];

  const fields=[
    { section: "Details", fields: [
      { name: "ResourceId", label: "ID", component: FieldValue, options: { maxLength: 36 } }, 
      { name: "ClientAccountId", label: "Client", component: ResourceRef, options: { type: 'Client' } },
      { name: "ContainerType", label: "Container" },
      { name: "ExecutionType", label: "Execution" },
      { name: "StackCreated", label: "Created", formatter: fmtIso8601AsLocalDateTime },
      { name: "StackUpdated", label: "Updated", formatter: fmtIso8601AsLocalDateTime },
    ]},
    { section: "Data", fields: [
      { name: "DataConfiguration.SourceDatasets", label: "Sources", formatter: fmtDataConfigurationRefs,
        component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: false } },
      { name: "DataConfiguration.TargetDataset", label: "Target", formatter: fmtDataConfigurationRefs, 
        component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: false } },
    ]},
    { section: "Controller", fields: [
      { name: "ControllerConfiguration.ContainerImage", label: "Image", component: ContainerChip,
        options: {
          owner: resource.ClientAccountId,
          account: resource.ControllerConfiguration?.RepoAccountId,
          repo: resource.ControllerConfiguration?.RepoName,
          image: resource.ControllerConfiguration?.ImageTag,
      }},
      { name: "ControllerConfiguration.ContainerMemory", label: "Memory" },
      { name: "ControllerConfiguration.LogLevel", label: "Log Level" },
    ]},
    { section: "Workload", fields: [
      { name: "WorkloadConfiguration.ContainerImage", label: "Image", component: ContainerChip,
        options: {
          owner: resource.ClientAccountId,
          account: resource.WorkloadConfiguration?.RepoAccountId,
          repo: resource.WorkloadConfiguration?.RepoName,
          image: resource.WorkloadConfiguration?.ImageTag,
      }},
      { name: "WorkloadConfiguration.ContainerMemory", label: "Memory" },
      { name: "WorkloadConfiguration.LogLevel", label: "Log Level" },
    ]},
    { section: "Cluster", fields: [
      { name: "ClusterConfiguration.InstanceType", label: "Instance Type" },
      { name: "ClusterConfiguration.MaxCapacity", label: "Max Capacity" },
      { name: "ClusterConfiguration.AmiId", label: "Image" },
    ]},
    { section: "Executions", 
      expandable: true, expanded: executionsExpanded, expand: expandExecutions, onExpandClick: handleExpandClick,
      footer: { 
        component: Executions,
        props: { workload: resource.ResourceId, title: null, columnDefs: executionsColumnDefs, prefName: 'workload' },
      }
    },
  ];

  return (
    <React.Fragment>
      <ResourceCard 
        resource={{...resource, ...dataConfiguration}} 
        fields={fields} 
        stack dashboard 
        actions={[{ 
          name: "Start Execution...",
          icon: StartExecutionIcon,
          onClick: handleStartExecutionDialogOpen,
          }]}
      />
      <StartExecutionDialog 
        resource={{...resource, ...dataConfiguration}}
        open={openStartExecution} 
        onCancel={handleStartExecutionDialogClose} 
        onExecute={handleStartExecution}/>
      <SnackbarAlert 
        key={'Start Execution'}
        open={showStartExecutionMessage}
        onClose={handleCloseStartExecutionMessage}
        {...startExecutionMessage}
      />
    </React.Fragment>    
  );
}


WorkloadCard.propTypes = {
  resource: PropTypes.object.isRequired,
};

WorkloadCard.defaultProps = {
};
